import React from 'react';
import { Box, Drawer, Typography } from '@mui/material';
import useMenteePeers from '../../../../../queries/useMenteePeers';
import { getUserDetails } from '../../../../../utils/orgName';
import { APP_HEADER_HEIGHT, UserProgramInfo } from '../utils/programTypes';

interface Props {
  open: boolean;
  onClose: () => void;
  program?: UserProgramInfo;
}

const UserProgramDrawer: React.FC<Props> = ({ open, onClose, program }) => {
  const { orgId, location } = getUserDetails();
  const { data: menteePeersData } = useMenteePeers({
    grpId: program?.id || '',
    orgId,
    location
  });

  if (!program) return null;

  const peerData = menteePeersData?.data?.data?.[0];
  const progress = typeof peerData?.avgGoalProgress === 'number' ? peerData?.avgGoalProgress : program.progress;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        BackdropProps: {
          sx: {
            top: APP_HEADER_HEIGHT,
            height: `calc(100% - ${APP_HEADER_HEIGHT}px)`
          }
        }
      }}
      PaperProps={{
        sx: {
          width: 400,
          background: '#151515',
          borderLeft: '1px solid #2A2A2A',
          p: 3,
          top: APP_HEADER_HEIGHT,
          height: `calc(100% - ${APP_HEADER_HEIGHT}px)`
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            sx={{
              px: 1,
              py: 0.5,
              background: '#F15A26',
              borderRadius: '20px',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 400, fontFamily: 'Inter' }}>
              {program.type}
            </Typography>
          </Box>
          <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
            {program.roles.join(' / ')}
          </Typography>
        </Box>

        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: 22,
            fontWeight: 600,
            textTransform: 'capitalize',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {program.name}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400, fontFamily: 'Inter' }}>
            Program Progress: {typeof progress === 'number' ? `${progress}%` : 'N/A'}
          </Typography>
          <Box sx={{ height: 6, background: '#2A2A2A', borderRadius: 3, overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${Math.min(Math.max(progress || 0, 0), 100)}%`,
                background: '#F0B80D'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, fontFamily: 'Inter' }}>Peers</Typography>
          {peerData?.menteeDetails?.length ? (
            peerData.menteeDetails.map((peer: any, idx: number) => (
              <Box
                key={peer.userId || idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  borderBottom: '1px solid #2A2A2A'
                }}
              >
                <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>{peer.displayName}</Typography>
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 600 }}>
                  {peer.goalProgress ?? 0}%
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ color: '#868686', fontSize: 12 }}>No peer data available.</Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default React.memo(UserProgramDrawer);
